import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import map from 'ramda/src/map'

import { Address } from '@ecm/data/Address'
import { Area } from '@ecm/data/Area'
import { Article } from '@ecm/data/Article'
import { createState, State } from '@ecm/data/State'
import { runEff } from '@ecm/effect'
import Benefits from '@ecm/ui/component/addresses/Benefits'
import BlogArticles from '@ecm/ui/component/addresses/BlogArticles'
import HowItWorks from '@ecm/ui/component/addresses/HowItWorks'
import MapWithPlaceholder from '@ecm/ui/component/addresses/MapWithPlaceholder'
import StateAddressList from '@ecm/ui/component/addresses/StateAddressList'
import TopSection from '@ecm/ui/component/addresses/TopSection'
import { BreadCrumb } from '@ecm/ui/element/Breadcrumbs'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'
import ReadMoreToggle from '@ecm/ui/element/ReadMoreToggle'
import useSmallScreen from '@ecm/ui/hook/UseSmallScreen'
import DefaultLayout from '@ecm/ui/layout/Default'
import { SelectedAddressesProvider } from '@ecm/ui/provider/SelectedAddressesProvider'
import { VisibleAddressesProvider } from '@ecm/ui/provider/VisibleAddressesProvider'

const DynamicStickyBanner = dynamic(() => import('@ecm/ui/component/StickyBanner'))

type StatePageProps = {
  articles: Article[]
  state: State
  breadCrumb: BreadCrumb[]
}

export const StatePage = ({ articles, state, breadCrumb }: StatePageProps) => {
  const isSmallScreen = useSmallScreen()

  const stateAddresses = state.areas
    .map(area => area.addresses)
    .flat()
    .reduce<Address[]>((result, currentAddress) => {
      if (result.filter(f => f.slug === currentAddress.slug).length) {
        return result
      }

      return [...result, currentAddress]
    }, [])

  return (
    <DefaultLayout
      hideFinalCta
      seoTitle={`${state.name} Address | Earth Class Mail`}
      seoDescription={`Get a real street address or PO box in ${state.name} for personal privacy or to register your business. Access your postal mail even when you're on the move.`}
      canonicalUrl={`addresses/${state.slug}`}
    >
      <Box
        sx={{
          background: `linear-gradient(180deg, ${Color.smoke.normal} 520px, rgba(255, 255, 255, 0) 750px)`,
        }}
      >
        <TopSection breadCrumb={breadCrumb} header={`${state.name} Virtual Address`}>
          <Typography variant="p3">
            {`Network of real ${state.name} addresses ready for you to receive your mail. `}
            <b>Use them for your virtual address, mail scanning, and mail forwarding.</b>
          </Typography>
          <Typography variant="p3">
            {`Need an address in ${state.name}? Earth Class Mail gives you the ability to have a mailing address in ${state.name}, 
            rent a PO Box, forward mail in ${state.name}, or check your mailbox with its virtual mailbox in ${state.name}. You can use your virtual address in 
            ${state.name} to register a business. ${state.name} virtual addresses can also help to keep your home address private.`}
          </Typography>
        </TopSection>

        <MainContainer>
          <SelectedAddressesProvider>
            <VisibleAddressesProvider initialAddresses={stateAddresses}>
              <Box
                bgcolor={{ md: Color.white.normal }}
                border={{ md: `1px solid ${Color.smoke.darker}` }}
                borderRadius="8px"
                overflow="hidden"
              >
                <Grid container borderRadius="8px">
                  <Grid item xs={12} md={6} lg={5} maxHeight={{ md: '600px' }}>
                    <StateAddressList
                      allAddresses={isSmallScreen ? stateAddresses : undefined}
                      showHeader
                    />
                  </Grid>
                  {isSmallScreen === false && (
                    <Grid item md={6} lg={7} height="600px">
                      <MapWithPlaceholder addresses={stateAddresses} />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </VisibleAddressesProvider>
          </SelectedAddressesProvider>
        </MainContainer>
      </Box>
      <HowItWorks />
      <Box bgcolor={Color.smoke.lightest}>
        {state.description && (
          <MainContainer
            /* eslint-disable sort-keys */
            sx={{ paddingBottom: { xs: '2em', md: '0' }, paddingTop: { xs: '3em', md: '6em' } }}
            /* eslint-disable sort-keys */
          >
            <Typography variant="h3" component="h2" margin="0 0 1em" maxWidth="600px">
              Why get an address in {state.name}
            </Typography>
            <ReadMoreToggle
              maxHeight={isSmallScreen ? '300px' : 'none'}
              color={Color.smoke.lightest}
            >
              <Typography variant="p3" columnGap="60px" sx={{ columns: { md: 2, xs: 1 } }}>
                {state.description}
              </Typography>
            </ReadMoreToggle>
          </MainContainer>
        )}

        <Benefits />
      </Box>

      <BlogArticles articles={articles} />

      <DynamicStickyBanner trackingId="stickySignupBannerAddress" eventName="addressOfferClick" />
    </DefaultLayout>
  )
}

export const getStaticProps: GetStaticProps = async ctx => {
  const stateSlug = ctx.params?.state_slug as string

  const areas = await runEff(eff => {
    return eff.getAreasByStateAbbreviation(stateSlug.toUpperCase())
  })

  const state = createState(stateSlug, areas)

  const breadCrumb: BreadCrumb[] = [
    { description: 'Addresses', href: '/addresses', position: 2 },
    { description: state.name, href: `/addresses/${state.slug}`, position: 3 },
  ]

  const randomArticles = await runEff(async eff => {
    try {
      const articlesMetadata = await eff.getArticlesMetadataByFieldValue('shouldLink')
      const limit = 3
      const min = limit
      const max = articlesMetadata.total - min // keep the difference between the total and possible max skip at the limit level
      const skip = Math.floor(Math.random() * (max - min) + min)
      const allArticles = await eff.getRelatedArticles('shouldLink', undefined, {
        limit: limit,
        order: '-fields.publishedDate',
        skip: skip,
      })
      return allArticles
    } catch (e) {
      return []
    }
  })

  return {
    props: {
      articles: randomArticles,
      breadCrumb,
      state,
    },
  }
}

export async function getStaticPaths() {
  const areas: Area[] = await runEff(eff => {
    return eff.getAreas()
  })

  const paths = map(a => ({ params: { state_slug: a.stateAbbreviation } }), areas)

  return {
    fallback: false,
    paths,
  }
}

export default StatePage
